/**
 * CodigoMunicipalResolver - Resolución de códigos INE/Catastro para geocodificación
 * 
 * Proporciona traducción bidireccional entre códigos INE y Catastro,
 * necesaria porque diferentes APIs usan diferentes sistemas:
 * - CartoCiudad: usa códigos INE
 * - Catastro WFS: usa códigos Catastro
 * - SIGPAC: usa HOJA (formato PPCCCA)
 * 
 * @module CodigoMunicipalResolver
 */

import { 
  EQUIVALENCIAS_INE_CATASTRO, 
  type EquivalenciaMunicipio 
} from '../data/municipios/equivalencias_ine_catastro';

// ============================================================================
// TIPOS
// ============================================================================

export interface CodigoResuelto {
  codIne: number;
  codCatastro: number;
  codProvincia: number;
  nombre: string;
  hojasSigpac: string | string[];
  tipoDiscrepancia?: 'capital' | 'offset_granada' | 'zona_cadiz_53' | 'segregacion' | 'normal';
}

export interface ResultadoBusqueda {
  encontrado: boolean;
  municipio?: CodigoResuelto;
  alternativas?: CodigoResuelto[];
  error?: string;
}

// ============================================================================
// CONSTANTES
// ============================================================================

/** Municipios de Cádiz en zona catastral 53 (en lugar de 11) */
const CADIZ_ZONA_53 = new Set([
  'ALGECIRAS', 'CASTELLAR DE LA FRONTERA', 'JIMENA DE LA FRONTERA',
  'LA LÍNEA DE LA CONCEPCIÓN', 'LOS BARRIOS', 'SAN ROQUE', 'TARIFA'
]);

/** Capitales provinciales (Catastro = 900) */
const _CAPITALES = new Map<number, string>([
  [4, 'ALMERÍA'], [11, 'CÁDIZ'], [14, 'CÓRDOBA'], [18, 'GRANADA'],
  [21, 'HUELVA'], [23, 'JAÉN'], [29, 'MÁLAGA'], [41, 'SEVILLA']
]);

// ============================================================================
// ÍNDICES EN MEMORIA (inicialización lazy)
// ============================================================================

let indiceIne: Map<string, EquivalenciaMunicipio> | null = null;
let indiceCatastro: Map<string, EquivalenciaMunicipio> | null = null;
let indiceNombre: Map<string, EquivalenciaMunicipio> | null = null;

function inicializarIndices(): void {
  if (indiceIne) return; // Ya inicializado
  
  indiceIne = new Map();
  indiceCatastro = new Map();
  indiceNombre = new Map();
  
  for (const mun of EQUIVALENCIAS_INE_CATASTRO) {
    // Clave compuesta provincia-código
    const claveIne = `${mun.codProvincia}-${mun.codIne}`;
    const claveCat = `${mun.codProvincia}-${mun.codCatastro}`;
    
    indiceIne.set(claveIne, mun);
    indiceCatastro.set(claveCat, mun);
    indiceNombre.set(normalizarNombre(mun.nombre), mun);
  }
}

// ============================================================================
// FUNCIONES DE NORMALIZACIÓN
// ============================================================================

function normalizarNombre(nombre: string): string {
  return nombre
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Quitar acentos
    .replace(/[^A-Z0-9\s]/g, '') // Solo alfanuméricos
    .replace(/\s+/g, ' ')
    .trim();
}

function determinarTipoDiscrepancia(mun: EquivalenciaMunicipio): CodigoResuelto['tipoDiscrepancia'] {
  if (mun.esCapital) return 'capital';
  if (mun.esSegregacion) return 'segregacion';
  if (mun.codProvincia === 18 && mun.codCatastro === mun.codIne + 1) return 'offset_granada';
  if (mun.codProvincia === 11 && CADIZ_ZONA_53.has(mun.nombre)) return 'zona_cadiz_53';
  if (mun.codIne !== mun.codCatastro) return 'normal'; // Otra discrepancia
  return undefined; // Códigos iguales
}

function toCodigoResuelto(mun: EquivalenciaMunicipio): CodigoResuelto {
  return {
    codIne: mun.codIne,
    codCatastro: mun.codCatastro,
    codProvincia: mun.codProvincia,
    nombre: mun.nombre,
    hojasSigpac: mun.hojasSigpac,
    tipoDiscrepancia: determinarTipoDiscrepancia(mun)
  };
}

// ============================================================================
// FUNCIONES PÚBLICAS
// ============================================================================

/**
 * Busca municipio por código INE
 */
export function buscarPorIne(codProvincia: number, codIne: number): ResultadoBusqueda {
  inicializarIndices();
  
  const clave = `${codProvincia}-${codIne}`;
  const mun = indiceIne!.get(clave);
  
  if (mun) {
    return { encontrado: true, municipio: toCodigoResuelto(mun) };
  }
  
  return { encontrado: false, error: `No encontrado: INE ${codProvincia}/${codIne}` };
}

/**
 * Busca municipio por código Catastro
 */
export function buscarPorCatastro(codProvincia: number, codCatastro: number): ResultadoBusqueda {
  inicializarIndices();
  
  const clave = `${codProvincia}-${codCatastro}`;
  const mun = indiceCatastro!.get(clave);
  
  if (mun) {
    return { encontrado: true, municipio: toCodigoResuelto(mun) };
  }
  
  return { encontrado: false, error: `No encontrado: Catastro ${codProvincia}/${codCatastro}` };
}

/**
 * Busca municipio por nombre (normalizado)
 */
export function buscarPorNombre(nombre: string): ResultadoBusqueda {
  inicializarIndices();
  
  const nombreNorm = normalizarNombre(nombre);
  const mun = indiceNombre!.get(nombreNorm);
  
  if (mun) {
    return { encontrado: true, municipio: toCodigoResuelto(mun) };
  }
  
  // Búsqueda parcial
  const alternativas: CodigoResuelto[] = [];
  indiceNombre!.forEach((m, key) => {
    if (alternativas.length < 5 && (key.includes(nombreNorm) || nombreNorm.includes(key))) {
      alternativas.push(toCodigoResuelto(m));
    }
  });
  
  if (alternativas.length > 0) {
    return { encontrado: false, alternativas, error: 'No encontrado exacto' };
  }
  
  return { encontrado: false, error: `No encontrado: "${nombre}"` };
}

/**
 * Convierte código INE a Catastro
 */
export function ineToCatastro(codProvincia: number, codIne: number): number | null {
  const resultado = buscarPorIne(codProvincia, codIne);
  return resultado.encontrado ? resultado.municipio!.codCatastro : null;
}

/**
 * Convierte código Catastro a INE
 */
export function catastroToIne(codProvincia: number, codCatastro: number): number | null {
  const resultado = buscarPorCatastro(codProvincia, codCatastro);
  return resultado.encontrado ? resultado.municipio!.codIne : null;
}

/**
 * Resuelve código para un servicio específico
 */
export function resolverParaServicio(
  codProvincia: number, 
  codigo: number, 
  _servicio: 'cartociudad' | 'catastro' | 'sigpac'
): CodigoResuelto | null {
  inicializarIndices();
  
  // Primero intentar como INE
  let resultado = buscarPorIne(codProvincia, codigo);
  
  // Si no encontrado, intentar como Catastro
  if (!resultado.encontrado) {
    resultado = buscarPorCatastro(codProvincia, codigo);
  }
  
  if (!resultado.encontrado) return null;
  
  return resultado.municipio!;
}

/**
 * Obtiene estadísticas de la base de datos
 */
export function getEstadisticas(): {
  total: number;
  capitales: number;
  segregaciones: number;
  offsetGranada: number;
  zonaCadiz53: number;
  discrepancias: number;
} {
  inicializarIndices();
  
  let capitales = 0, segregaciones = 0, offsetGranada = 0, zonaCadiz53 = 0, discrepancias = 0;
  
  for (const mun of EQUIVALENCIAS_INE_CATASTRO) {
    if (mun.esCapital) capitales++;
    if (mun.esSegregacion) segregaciones++;
    if (mun.codProvincia === 18 && mun.codCatastro === mun.codIne + 1) offsetGranada++;
    if (mun.codProvincia === 11 && CADIZ_ZONA_53.has(mun.nombre)) zonaCadiz53++;
    if (mun.codIne !== mun.codCatastro) discrepancias++;
  }
  
  return {
    total: EQUIVALENCIAS_INE_CATASTRO.length,
    capitales,
    segregaciones,
    offsetGranada,
    zonaCadiz53,
    discrepancias
  };
}

/**
 * Lista todos los municipios de una provincia
 */
export function getMunicipiosProvincia(codProvincia: number): CodigoResuelto[] {
  return EQUIVALENCIAS_INE_CATASTRO
    .filter(m => m.codProvincia === codProvincia)
    .map(toCodigoResuelto);
}
